(() => {
  const normalize = (value) => String(value ?? '').trim().toLocaleLowerCase();
  const words = (value) => normalize(value).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const split = (value) => String(value || '').split(',').map(normalize).filter(Boolean);
  const compareText = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  const sorts = ['discount', 'price-ascending', 'price-descending', 'newest'];
  const genders = ['Men', 'Women', 'Kids', 'Unisex'];
  const adultSizes = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];

  const genderValues = (value) => {
    const values = Array.isArray(value) ? value : String(value || '').split(',');
    const aliases = { men: 'Men', mens: 'Men', male: 'Men', women: 'Women', womens: 'Women', female: 'Women', kids: 'Kids', kid: 'Kids', children: 'Kids', boy: 'Kids', boys: 'Kids', girl: 'Kids', girls: 'Kids', baby: 'Kids', babies: 'Kids', unisex: 'Unisex' };
    return [...new Set(values.flatMap((entry) => {
      const label = words(entry).replace(/^gender /, '').replace(/ s$/, 's');
      if (/^(?:(?:kids?|children|bab(?:y|ies)) unisex|unisex (?:kids?|children|bab(?:y|ies)))$/.test(label)) return ['Kids', 'Unisex'];
      return aliases[label] ? [aliases[label]] : [];
    }))];
  };

  class ClearanceCatalog {
    constructor(products, config) {
      this.categories = (config.categories || []).filter((category) => category.title?.trim()).map((category) => ({
        ...category, matches: [category.title, ...split(category.matches)].map(words),
      }));
      const sizeNames = split(config.sizeOptions);
      this.colorNames = split(config.colorOptions || 'color, colour');
      this.products = products.map((product) => {
        const sizeIndex = product.options.findIndex((option) => sizeNames.includes(normalize(option)));
        const variants = product.variants.filter((variant) => variant.available &&
          (variant.inventory === null || variant.inventory >= variant.minimum));
        const explicit = genderValues(product.gender);
        const assigned = explicit.length ? explicit : genderValues(product.tags || []);
        const category = this.categoryFor(product);
        const childAudience = assigned.includes('Unisex') &&
          (assigned.includes('Kids') || genderValues(product.tags || []).includes('Kids') ||
            /\b(?:baby|babies|kids?|boys?|girls?)\b/.test(words(product.title)));
        const childSized = childAudience || (assigned.includes('Unisex') && sizeIndex >= 0 && !this.skipSize(category) &&
          product.variants.some((variant) => ['Baby', 'Kids'].includes(
            this.sizeGroup(variant.options[sizeIndex] || '', category, 'Kids').title)));
        const audiences = assigned.flatMap((value) => value === 'Unisex'
          ? (childSized ? ['Kids', 'Unisex'] : ['Men', 'Women', 'Unisex']) : [value])
          .filter((value) => !childSized || !['Men', 'Women'].includes(value));
        return { ...product, brand: product.brand?.trim() || 'Unbranded', category, genders: [...new Set(audiences)], sizeIndex, variants };
      }).filter((product) => product.variants.length);
      this.byId = new Map(this.products.map((product) => [product.id, product]));
    }

    categoryFor(product) {
      // A merchant's explicit category outranks inference from product type or tags.
      const explicit = Array.isArray(product.category) ? product.category : [product.category];
      const sources = [...explicit, product.type, product.customType, ...(product.tags || [])];
      for (const source of sources) {
        if (typeof source !== 'string' || !source.trim()) continue;
        const normalized = words(source);
        const match = this.categories.find((category) => category.matches.some((term) =>
          term && (` ${normalized} `).includes(` ${term} `)));
        if (match) return match.title;
        if (explicit.includes(source)) return source.trim();
      }
      return product.type?.trim() || 'Other';
    }

    skipSize(category) {
      return Boolean(this.categories.find((entry) => entry.title === category)?.skipSize);
    }

    variantsFor(product, size = '') {
      if (!size || this.skipSize(product.category)) return product.variants;
      return product.variants.filter((variant) => this.sizeFor(product, variant) === size);
    }

    sizeFor(product, variant) {
      return product.sizeIndex < 0 ? 'One size' : (variant.options[product.sizeIndex] || 'One size');
    }

    pool({ gender = '', brand = '', category = '', size = '' } = {}) {
      return this.products.filter((product) => (!gender || product.genders.includes(gender)) && (!brand || product.brand === brand) &&
        (!category || product.category === category) && this.variantsFor(product, size).length);
    }

    validState(state) {
      const result = { ...state, gender: '', brand: '', category: '', size: '' };
      for (const key of ['gender', 'brand', 'category', 'size']) {
        if (!state[key]) continue;
        if (key === 'size' && this.skipSize(result.category)) continue;
        if (this.counts(key, result).has(state[key])) result[key] = state[key];
      }
      return result;
    }

    counts(key, state) {
      const counts = new Map();
      this.pool(state).forEach((product) => {
        const values = key === 'size'
          ? new Set(product.variants.map((variant) => this.sizeFor(product, variant)))
          : key === 'gender' ? product.genders : [product[key]];
        values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
      });
      return counts;
    }

    representative(product, size, sort) {
      return this.variantsFor(product, size).reduce((best, variant) => {
        if (!best) return variant;
        if (sort === 'discount' && this.discountRatio(best) !== this.discountRatio(variant)) {
          return this.discountRatio(variant) > this.discountRatio(best) ? variant : best;
        }
        return variant.price < best.price ? variant : best;
      }, null);
    }

    discount(variant) {
      return Math.round(this.discountRatio(variant) * 100);
    }

    discountRatio(variant) {
      return variant?.compare > variant.price ? (variant.compare - variant.price) / variant.compare : 0;
    }

    displayName(product, variant) {
      const type = product.customType?.trim() || product.type?.trim() || product.category || 'Product';
      const colorIndex = product.options.findIndex((option) => this.colorNames.includes(normalize(option)));
      const color = (colorIndex >= 0 ? variant?.options[colorIndex] : '') || product.color || '';
      return [type, color].filter(Boolean).join(' · ');
    }

    sizeGroup(size, category, gender = '') {
      const normalized = size.toUpperCase().trim().replace(/\s+/g, ' ');
      const adultIndex = adultSizes.indexOf(normalized.replace(/^2XL$/, 'XXL').replace(/^3XL$/, 'XXXL'));
      if (adultIndex >= 0) return { title: 'Adult sizes', order: adultIndex };
      const age = normalized.match(/^(\d+(?:\.\d+)?)\s*(M|MO|MOS|MONTHS?|Y|YR|YRS|YEARS?|T)?(?:\s*(?:[-–—/]|TO)\s*(\d+(?:\.\d+)?)\s*(M|MO|MOS|MONTHS?|Y|YR|YRS|YEARS?|T)?)?$/);
      if (age && (age[2] || age[4])) {
        const startUnit = age[2] || age[4];
        const endUnit = age[4] || startUnit;
        const startMultiplier = /^(M|MO|MOS|MONTH)/.test(startUnit) ? 1 : 12;
        const endMultiplier = /^(M|MO|MOS|MONTH)/.test(endUnit) ? 1 : 12;
        return { title: startMultiplier === 1 && endMultiplier === 1 ? 'Baby' : 'Kids', order: Number(age[1]) * startMultiplier, end: Number(age[3] || age[1]) * endMultiplier };
      }
      if (/shoe|footwear/i.test(category)) return { title: 'Shoe sizes', order: parseFloat(normalized) || 0 };
      const kidsAge = normalized.match(/^(\d{1,2})(?:\s*(?:[-–—/]|TO)\s*(\d{1,2}))?$/);
      if (gender === 'Kids' && kidsAge && Number(kidsAge[1]) <= 18 && Number(kidsAge[2] || kidsAge[1]) <= 18) {
        return { title: 'Kids', order: Number(kidsAge[1]) * 12, end: Number(kidsAge[2] || kidsAge[1]) * 12 };
      }
      const waist = normalized.match(/^(?:W(?:AIST)?\s*)?(\d{2})(?:\s*(?:IN|INCHES|"))?$/);
      if (waist && Number(waist[1]) >= 24 && Number(waist[1]) <= 42) return { title: 'Waist sizes', order: Number(waist[1]) };
      return { title: /^(ONE SIZE|OS|O\/S|FREE SIZE)$/.test(normalized) ? 'One size' : 'Other sizes', order: 0 };
    }

    sizeGroups(state) {
      const groups = new Map();
      this.counts('size', { ...state, size: '' }).forEach((count, size) => {
        const group = this.sizeGroup(size, state.category, state.gender);
        if (state.gender === 'Kids' && ['Adult sizes', 'Waist sizes'].includes(group.title)) return;
        if (['Men', 'Women'].includes(state.gender) && ['Baby', 'Kids'].includes(group.title)) return;
        if (!groups.has(group.title)) groups.set(group.title, []);
        groups.get(group.title).push({ size, count, ...group });
      });
      const order = ['Adult sizes', 'Waist sizes', 'Baby', 'Kids', 'Shoe sizes', 'One size', 'Other sizes'];
      return [...groups].sort(([a], [b]) => order.indexOf(a) - order.indexOf(b)).map(([title, sizes]) => ({
        title, sizes: sizes.sort((a, b) => a.order - b.order || (a.end || 0) - (b.end || 0) || compareText(a.size, b.size)),
      }));
    }

    stock(variants) {
      return variants.some((variant) => variant.inventory === null) ? null :
        variants.reduce((total, variant) => total + variant.inventory, 0);
    }

    list(state) {
      const rows = this.pool(state).map((product) => ({ product, variant: this.representative(product, state.size, state.sort) }));
      return rows.sort((a, b) => {
        let difference = 0;
        if (state.sort === 'price-ascending') difference = a.variant.price - b.variant.price;
        else if (state.sort === 'price-descending') difference = b.variant.price - a.variant.price;
        else if (state.sort === 'newest') difference = Number(b.product.published) - Number(a.product.published);
        else difference = this.discountRatio(b.variant) - this.discountRatio(a.variant);
        return difference || compareText(a.product.title, b.product.title) || compareText(a.product.id, b.product.id);
      });
    }
  }

  // The data model can be tested without a browser or Shopify credentials.
  if (typeof module !== 'undefined' && module.exports) module.exports = { ClearanceCatalog };
  if (typeof customElements === 'undefined' || customElements.get('clearance-collection')) return;

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  class ClearanceCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.state = this.readState();
      this.letter = 'All';
      this.query = '';
      this.events = new AbortController();
      const signal = this.events.signal;
      this.addEventListener('click', this.onClick.bind(this), { signal });
      this.addEventListener('input', this.onInput.bind(this), { signal });
      this.addEventListener('change', this.onChange.bind(this), { signal });
      this.addEventListener('submit', (event) => {
        if (event.target.closest('[data-dialog]')) this.pendingCart = true;
      }, { capture: true, signal });
      window.addEventListener('popstate', () => {
        this.state = this.readState();
        if (this.catalog) {
          this.state = this.catalog.validState(this.state);
          this.writeState(true);
          this.resetLimit(); this.render();
        }
      }, { signal });
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.id === `shopify-section-${this.catalogSectionId}`) this.load();
      }, { signal });
      this.dialog = this.querySelector('[data-dialog]');
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog();
        }
      }, { signal });
      this.dialog.addEventListener('cancel', (event) => {
        if (this.pendingCart) event.preventDefault();
      }, { signal });
      if (typeof subscribe === 'function') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!this.pendingCart || !this.dialogProduct?.variants.some((variant) => variant.id === String(event.productVariantId))) return;
          this.pendingCart = false;
          this.closeDialog();
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cart) cart.setActiveElement(this.opener);
          this.toast('Added to cart');
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (this.dialogProduct?.variants.some((variant) => variant.id === String(event.productVariantId))) this.pendingCart = false;
        });
      }
      const header = document.querySelector('.section-header');
      const updateOffset = () => {
        const rect = header?.getBoundingClientRect();
        this.style.setProperty('--clearance-header-height', `${rect ? Math.max(0, Math.min(rect.bottom, rect.height)) : 0}px`);
      };
      window.addEventListener('scroll', updateOffset, { passive: true, signal });
      window.addEventListener('resize', updateOffset, { passive: true, signal });
      if (header && typeof ResizeObserver !== 'undefined') {
        this.headerObserver = new ResizeObserver(updateOffset);
        this.headerObserver.observe(header);
      }
      updateOffset();
      this.load();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.loadingController?.abort();
      this.headerObserver?.disconnect();
      this.formObserver?.disconnect();
      this.unsubscribeCart?.();
      this.unsubscribeError?.();
      clearTimeout(this.toastTimeout);
      this.dialog?.close();
      this.initialized = false;
    }

    find(selector) { return this.querySelector(selector); }

    readState() {
      const params = new URL(window.location.href).searchParams;
      const sort = params.get('clearance_sort');
      return {
        gender: genders.includes(params.get('clearance_gender')) ? params.get('clearance_gender') : '',
        brand: params.get('clearance_brand') || '', category: params.get('clearance_category') || '',
        allBrands: params.get('clearance_allBrands') === 'true',
        size: params.get('clearance_size') || '', sort: sorts.includes(sort) ? sort : 'discount',
      };
    }

    writeState(replace = false) {
      const url = new URL(window.location.href);
      Object.entries(this.state).forEach(([key, value]) => {
        if (value && !(key === 'sort' && value === 'discount')) url.searchParams.set(`clearance_${key}`, value);
        else url.searchParams.delete(`clearance_${key}`);
      });
      url.searchParams.delete('page');
      if (url.href !== window.location.href) window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
    }

    resetLimit() {
      this.limit = this.state.gender || this.state.brand || this.state.category || this.state.size ? this.config.pageSize : this.config.initialProducts;
    }

    async load() {
      this.loadingController?.abort();
      const controller = new AbortController();
      this.loadingController = controller;
      this.catalog = null;
      this.find('[data-controls]').hidden = true;
      this.find('[data-bar]').hidden = true;
      this.find('[data-live]').hidden = true;
      this.find('[data-more-wrap]').hidden = true;
      this.find('[data-empty]').hidden = true;
      this.find('[data-loading]').hidden = false;
      this.find('[data-error]').hidden = true;
      try {
        const source = document.querySelector(`script[data-clearance-catalog][data-collection-id="${this.dataset.collectionId}"]`);
        if (!source) throw new Error('Missing clearance product data section.');
        let first = JSON.parse(source.textContent);
        this.catalogSectionId = first.sectionId;
        if (first.page !== 1 || first.total !== first.unfilteredTotal) first = await this.fetchPage(1, controller.signal);
        if (first.total > 25000) throw new Error('Collection exceeds the Liquid pagination limit.');
        // Show real first-page cards immediately; enable global filters only when complete.
        const preview = new ClearanceCatalog(first.products, this.config);
        const previewRows = preview.list({ sort: this.state.sort }).slice(0, this.config.initialProducts);
        this.find('[data-count]').textContent = 'First products · Full filters are loading…';
        this.find('[data-loading]').textContent = 'Preparing gender, brand and size filters…';
        this.find('[data-grid]').replaceChildren(...previewRows.map((row) => this.card(row, preview, true)));
        this.find('[data-grid]').hidden = !previewRows.length;
        this.find('[data-fallback]').hidden = Boolean(previewRows.length);
        this.find('[data-grid]').setAttribute('aria-busy', 'true');
        const pages = [first];
        // Bound concurrency while first-page cards remain visible.
        for (let page = 2; page <= first.pages; page += 2) {
          const requests = [this.fetchPage(page, controller.signal)];
          if (page + 1 <= first.pages) requests.push(this.fetchPage(page + 1, controller.signal));
          pages.push(...await Promise.all(requests));
        }
        if (controller.signal.aborted || !this.isConnected) return;
        const products = [...new Map(pages.flatMap((page) => page.products).map((product) => [product.id, product])).values()];
        if (products.length !== first.total || products.some((product) => !product.variantsComplete)) {
          throw new Error('Incomplete catalog; retaining the server-rendered collection.');
        }
        this.catalog = new ClearanceCatalog(products, this.config);
        this.state = this.catalog.validState(this.state);
        this.writeState(true);
        this.resetLimit();
        this.find('[data-controls]').hidden = false;
        this.find('[data-fallback]').hidden = true;
        this.find('[data-grid]').hidden = false;
        this.render();
      } catch (error) {
        if (controller.signal.aborted) return;
        controller.abort();
        console.error('[Clearance]', error);
        this.catalog = null;
        this.find('[data-controls]').hidden = true;
        this.find('[data-grid]').hidden = true;
        this.find('[data-fallback]').hidden = false;
        this.find('[data-count]').textContent = '';
        this.find('[data-error]').hidden = false;
      } finally {
        if (this.loadingController === controller) {
          this.find('[data-loading]').hidden = true;
          this.find('[data-grid]').removeAttribute('aria-busy');
        }
      }
    }

    async fetchPage(page, signal) {
      const url = new URL(window.location.href);
      // Retain locale, preview_theme_id and the alternate template view.
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('filter.') || key.startsWith('clearance_') || ['sort_by', 'page', 'section_id', 'sections'].includes(key)) url.searchParams.delete(key);
      });
      url.searchParams.set('section_id', this.catalogSectionId);
      url.searchParams.set('page', page);
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('script[data-clearance-catalog]');
      if (!source) throw new Error('Catalog section was not returned.');
      const data = JSON.parse(source.textContent);
      if (data.page !== page || String(data.collectionId) !== this.dataset.collectionId) throw new Error('Unexpected catalog page.');
      return data;
    }

    button(text, action, value = '', className = 'clearance__chip', count, selected) {
      const button = element('button', className, text);
      button.type = 'button';
      button.dataset.action = action;
      button.dataset.value = value;
      if (selected !== undefined) button.setAttribute('aria-pressed', String(selected));
      if (count !== undefined) button.append(element('span', '', String(count)));
      return button;
    }

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button)) return;
      const { action, value = '' } = button.dataset;
      if (action === 'retry') { this.load(); return; }
      if (action === 'close') { this.closeDialog(); return; }
      if (!this.catalog) return;
      if (action === 'add') { this.openProduct(value, button); return; }
      if (action === 'letter') { this.letter = value; this.renderBrands(); return; }
      if (action === 'more') {
        const previous = this.limit;
        this.limit += this.config.pageSize;
        this.renderGrid();
        this.find('[data-grid]').children[previous]?.querySelector('a')?.focus({ preventScroll: true });
        return;
      }
      if (action === 'gender') {
        this.state = { gender: value, brand: '', category: '', size: '', sort: this.state.sort };
        this.query = ''; this.letter = 'All'; this.find('[data-brand-search]').value = '';
      } else if (action === 'brand') {
        this.state.brand = value;
        this.state.allBrands = !value;
        this.state.size = '';
        if (value && this.state.category && !this.catalog.pool(this.state).length) this.state.category = '';
        if (!value) { this.query = ''; this.letter = 'All'; this.find('[data-brand-search]').value = ''; }
      } else if (action === 'category') {
        this.state.category = value;
        this.state.size = '';
      } else if (action === 'size') this.state.size = value;
      else if (action === 'clear') {
        this.state = { gender: '', brand: '', category: '', size: '', sort: this.state.sort };
        this.query = ''; this.letter = 'All'; this.find('[data-brand-search]').value = '';
      } else return;
      this.resetLimit();
      this.writeState();
      this.render();
      let target = '[data-count]';
      if (action === 'gender') target = '[data-step="brand"] h2';
      else if (action === 'brand') target = '[data-step="category"] h2';
      else if (action === 'category' && !this.catalog.skipSize(value)) target = value ? '[data-step="size"] h2' : '[data-step="category"] h2';
      else if (action === 'size' && !value) target = '[data-step="size"] h2';
      else if (action === 'clear') target = '[data-step="gender"] h2';
      const focus = this.find(target);
      focus.focus({ preventScroll: true });
      focus.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: target === '[data-count]' ? 'start' : 'nearest' });
    }

    onInput(event) {
      if (event.target.matches('[data-brand-search]')) { this.query = event.target.value; this.renderBrands(); }
    }

    onChange(event) {
      if (event.target.matches('[data-sort]')) {
        this.state.sort = event.target.value;
        this.resetLimit(); this.writeState(); this.renderGrid();
      } else if (event.target.matches('[data-variant-select]')) this.updateVariant();
    }

    step(key, value, hint = '', dim = false) {
      const layer = this.find(`[data-step="${key}"]`);
      layer.classList.toggle('is-done', Boolean(value));
      layer.classList.toggle('is-dim', dim);
      this.find(`[data-body="${key}"]`).hidden = Boolean(value) || dim;
      const summary = this.find(`[data-summary="${key}"]`);
      summary.hidden = !value;
      summary.querySelector('span').textContent = key === 'size' ? `Size ${value}` : value;
      const hintNode = this.find(`[data-hint="${key}"]`);
      if (hintNode) {
        hintNode.textContent = hint;
        hintNode.hidden = Boolean(value);
      }
    }

    render() {
      this.renderGenders(); this.renderBrands(); this.renderCategories(); this.renderSizes(); this.renderGrid();
      const live = this.find('[data-live]');
      const stock = this.catalog.stock(this.catalog.products.flatMap((product) => product.variants));
      live.hidden = !this.config.showStockTotal;
      live.textContent = stock === null ? `${this.catalog.products.length} styles available` : `${stock.toLocaleString()} pieces left`;
    }

    renderGenders() {
      const counts = this.catalog.counts('gender', {});
      this.find('[data-options="gender"]').replaceChildren(...genders.map((gender) => {
        const button = this.button(gender, 'gender', gender, 'clearance__chip', counts.get(gender) || 0, gender === this.state.gender);
        button.disabled = !counts.get(gender);
        return button;
      }));
      this.step('gender', this.state.gender, 'Who are you shopping for?');
    }

    categoryOrder(counts) {
      const order = this.catalog.categories.map((category) => category.title);
      return [...counts].sort(([a], [b]) => {
        const ai = order.indexOf(a), bi = order.indexOf(b);
        return (ai < 0 ? Infinity : ai) - (bi < 0 ? Infinity : bi) || compareText(a, b);
      });
    }

    renderBrands() {
      const counts = this.catalog.counts('brand', { gender: this.state.gender, category: this.state.category });
      const letters = ['All', ...[...new Set([...counts.keys()].map((brand) => brand.charAt(0).toLocaleUpperCase()))].sort(compareText)];
      if (!letters.includes(this.letter)) this.letter = 'All';
      const focusedLetter = document.activeElement?.closest('[data-letters]') ? document.activeElement.dataset.value : null;
      this.find('[data-letters]').replaceChildren(...letters.map((letter) => this.button(letter, 'letter', letter, '', undefined, this.letter === letter)));
      const choices = [...counts].sort(([a], [b]) => compareText(a, b)).filter(([brand]) =>
        (this.letter === 'All' || brand.toLocaleUpperCase().startsWith(this.letter)) && normalize(brand).includes(normalize(this.query)));
      this.find('[data-options="brand"]').replaceChildren(
        this.button('All', 'brand', '', 'clearance__chip', undefined, Boolean(this.state.allBrands)),
        ...(choices.length
        ? choices.map(([brand, count]) => this.button(brand || 'Unbranded', 'brand', brand, 'clearance__chip', count, brand === this.state.brand))
        : [element('p', 'clearance__info', 'No brand found.')]));
      this.step('brand', this.state.brand, this.state.gender ? (this.state.category ? `Brands with ${this.state.category}` : '') : 'Choose Men, Women, Kids or Unisex first', !this.state.gender);
      if (focusedLetter !== null) [...this.find('[data-letters]').children].find((button) => button.dataset.value === focusedLetter)?.focus();
    }

    renderCategories() {
      const counts = this.catalog.counts('category', { gender: this.state.gender, brand: this.state.brand });
      this.find('[data-options="category"]').replaceChildren(
        this.button('All', 'category', '', 'clearance__chip', undefined, !this.state.category),
        ...this.categoryOrder(counts).map(([title, count]) => this.button(title, 'category', title, 'clearance__chip', count, title === this.state.category)));
      this.step('category', this.state.category, this.state.brand ? `What does ${this.state.brand} have on clearance` : 'Choose a brand or All to browse categories', !this.state.brand && !this.state.allBrands);
    }

    renderSizes() {
      const target = this.find('[data-options="size"]');
      if (!this.state.category) {
        this.step('size', '', 'Choose a category first', true); target.replaceChildren(); return;
      }
      if (this.catalog.skipSize(this.state.category)) {
        this.step('size', '', '');
        target.replaceChildren(element('p', 'clearance__info', 'No size filter needed. Choose any product options when adding to cart.')); return;
      }
      target.replaceChildren(this.button('All sizes', 'size', '', 'clearance__chip', undefined, !this.state.size),
        ...this.catalog.sizeGroups(this.state).map(({ title, sizes }) => {
          const group = element('div', 'clearance__size-group');
          group.append(element('h3', '', title));
          const chips = element('div', 'clearance__chips');
          chips.append(...sizes.map(({ size, count }) => this.button(size, 'size', size, 'clearance__chip', count, size === this.state.size)));
          group.append(chips);
          return group;
        }));
      this.step('size', this.state.size, `Available ${this.state.category.toLowerCase()} sizes${this.state.brand ? ` for ${this.state.brand}` : ''}`);
      target.append(element('p', 'clearance__size-hint', 'Swipe to see more sizes →'));
    }

    renderGrid() {
      const rows = this.catalog.list(this.state);
      const any = this.state.gender || this.state.brand || this.state.category || this.state.size;
      this.find('[data-bar]').hidden = !any;
      this.find('[data-sort]').value = this.state.sort;
      const tags = [];
      [['gender', 'Gender'], ['brand', 'Brand'], ['category', 'Category'], ['size', 'Size']].forEach(([key, label]) => {
        if (!this.state[key]) return;
        const button = this.button(this.state[key], key, '', 'clearance__tag');
        button.append(element('span', '', '×'));
        button.setAttribute('aria-label', `Remove ${label.toLowerCase()} ${this.state[key]}`);
        tags.push(button);
      });
      if (any) tags.push(this.button('Clear all', 'clear', '', 'clearance__change'));
      this.find('[data-tags]').replaceChildren(...tags);
      this.find('[data-count]').textContent = any
        ? `${rows.length} items${this.state.size ? ` in size ${this.state.size}` : ''} · Showing ${Math.min(rows.length, this.limit)}`
        : `${rows.length} clearance items. Choose Men, Women, Kids or Unisex to start.`;
      this.find('[data-grid]').replaceChildren(...rows.slice(0, this.limit).map((row) => this.card(row)));
      this.find('[data-empty]').hidden = Boolean(rows.length);
      this.find('[data-empty-size]').hidden = !this.state.size;
      this.find('[data-more-wrap]').hidden = rows.length <= this.limit;
    }

    price(variant, label = '') {
      const price = element('p', 'clearance-card__price');
      if (label) price.append(element('small', '', label));
      price.append(element('strong', '', variant.money));
      if (variant.compare > variant.price) price.append(element('s', '', variant.compareMoney));
      return price;
    }

    card({ product, variant }, catalog = this.catalog, preview = false) {
      const name = catalog.displayName(product, variant);
      const card = element('article', 'clearance-card');
      const media = element('div', 'clearance-card__media');
      const link = element('a', 'clearance-card__image');
      const url = new URL(product.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      link.href = url.pathname + url.search;
      link.setAttribute('aria-label', name);
      if (product.image) {
        const image = element('img');
        image.src = product.image;
        image.alt = product.imageAlt || name;
        image.width = 540; image.height = 720; image.loading = 'lazy'; image.decoding = 'async';
        image.sizes = '(min-width: 760px) 255px, 50vw';
        image.srcset = [180, 360, 540, 720].map((width) => {
          const imageUrl = new URL(product.image, window.location.origin);
          imageUrl.searchParams.set('width', width);
          return `${imageUrl.href} ${width}w`;
        }).join(', ');
        link.append(image);
      } else link.append(element('span', 'clearance__info', name));
      media.append(link);
      const discount = catalog.discount(variant);
      if (discount) media.append(element('span', 'clearance-card__discount', `−${discount}%`));
      const variants = catalog.variantsFor(product, this.state.size);
      const stock = catalog.stock(variants);
      if (stock !== null && stock > 0 && stock <= this.config.lowStock) media.append(element('span', 'clearance-card__stock', `Only ${stock} left${this.state.size ? ` in ${this.state.size}` : ''}`));
      const add = this.button('+', 'add', product.id, 'clearance-card__add');
      add.setAttribute('aria-label', `Choose options for ${name}`);
      add.disabled = preview;
      add.setAttribute('aria-haspopup', 'dialog');
      media.append(add);
      const heading = element('h3', 'clearance-card__title');
      const titleLink = element('a', '', name); titleLink.href = link.href; heading.append(titleLink);
      const varies = variants.some((other) => other.price !== variant.price);
      const priceLabel = varies ? (variants.some((other) => other.price < variant.price) ? 'Selected option' : 'From') : '';
      card.append(media, element('p', 'clearance-card__brand', `${product.brand} · ${product.category}`), heading, this.price(variant, priceLabel));
      return card;
    }

    openProduct(id, opener) {
      const product = this.catalog.byId.get(id);
      if (!product) return;
      if (product.requiresProductPage) { window.location.assign(product.url); return; }
      this.opener = opener;
      this.dialogProduct = product;
      this.pendingCart = false;
      this.find('[data-dialog-title]').textContent = this.catalog.displayName(product, this.catalog.representative(product, this.state.size, this.state.sort));
      const image = this.find('[data-dialog-image]');
      image.hidden = !product.image;
      if (product.image) { image.src = product.image; image.alt = this.find('[data-dialog-title]').textContent; }
      else image.removeAttribute('src');
      this.find('[data-dialog-link]').href = product.url;
      const content = this.find('[data-form-template]').content.cloneNode(true);
      const select = content.querySelector('[data-variant-select]');
      const variants = this.catalog.variantsFor(product, this.state.size);
      if (variants.length > 1) {
        const placeholder = new Option('Choose an option', '', true, true);
        placeholder.disabled = true;
        select.append(placeholder);
      }
      variants.forEach((variant) => select.append(new Option(variant.title === 'Default Title' ? 'One size' : variant.title, variant.id)));
      content.querySelector('[name="properties[_subtitle]"]').value = product.subtitle || '';
      this.formObserver?.disconnect();
      this.find('[data-dialog-form]').replaceChildren(content);
      this.updateVariant();
      const submit = this.find('[data-dialog-form] [type="submit"]');
      this.formObserver = new MutationObserver(() => {
        // Dawn logs transport errors without presenting a message; keep this dialog actionable.
        if (this.pendingCart && !submit.classList.contains('loading')) {
          this.pendingCart = false;
          const form = this.find('[data-dialog-form] product-form');
          form.handleErrorMessage('We couldn’t confirm the cart update. Check your cart before trying again.');
        }
      });
      this.formObserver.observe(submit, { attributes: true, attributeFilter: ['class'] });
      this.dialog.showModal();
      select.focus();
    }

    updateVariant() {
      const select = this.find('[data-variant-select]');
      const variant = this.dialogProduct.variants.find((entry) => entry.id === select.value);
      const submit = this.find('[data-dialog-form] [type="submit"]');
      submit.disabled = !variant;
      const price = this.find('[data-dialog-price]');
      price.replaceChildren(...(variant ? this.price(variant).childNodes : []));
      if (variant) {
        this.find('[data-dialog-title]').textContent = this.catalog.displayName(this.dialogProduct, variant);
        this.find('[data-dialog-image]').alt = this.find('[data-dialog-title]').textContent;
        this.find('[data-dialog-form] [name="quantity"]').value = variant.minimum;
        const link = new URL(this.dialogProduct.url, window.location.origin);
        link.searchParams.set('variant', variant.id);
        this.find('[data-dialog-link]').href = link.pathname + link.search;
        submit.querySelector('span').textContent = variant.minimum > 1 ? `Add ${variant.minimum} to cart` : 'Add to cart';
        this.find('[data-dialog-form] product-form').handleErrorMessage();
      }
    }

    closeDialog() {
      if (this.pendingCart) return;
      this.dialog.close();
      this.opener?.focus({ preventScroll: true });
    }

    toast(message) {
      const toast = this.find('[data-toast]');
      toast.textContent = message; toast.hidden = false;
      clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => { toast.hidden = true; }, 2500);
    }
  }
  customElements.define('clearance-collection', ClearanceCollection);
})();
